import React, { useState, useEffect } from 'react';
import { emailTemplateService, EmailTemplate } from '../services/emailTemplateService';

interface EmailTemplateSelectorProps {
  // Use a generic callback that accepts any template-like object
  onTemplateSelect: (template: any) => void;
  selectedTemplateId?: string;
  userId: string;
}

const EmailTemplateSelector: React.FC<EmailTemplateSelectorProps> = ({
  onTemplateSelect,
  selectedTemplateId,
  userId,
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTemplates();
  }, [userId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      // Fetch all templates from the emailTemplateService
      const templateList = await emailTemplateService.getAllTemplates();
      setTemplates(templateList);

      if (selectedTemplateId) {
        const template = templateList.find(t => t.id === selectedTemplateId);
        if (template) {
          setSelectedTemplate(template);
        }
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    onTemplateSelect(template);
  };

  // Filter templates based on search query
  const filteredTemplates = templates.filter(template => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Choose Email Template
        </label>
        <p className="text-sm text-gray-500 mb-4">
          Select a template to use for your automation email.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Template Count */}
      <div className="text-sm text-gray-500">
        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
      </div>
 
      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 mt-4 mb-2">
            {searchQuery ? 'No templates match your search' : 'No templates found'}
          </p>
          <p className="text-sm text-gray-400">
            Create templates in the Email Templates section
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[450px] overflow-y-auto pr-1">
          {[...filteredTemplates].sort((a, b) => {
            // Service already returns them sorted, but this ensures robustness if logic changes
            const orderA = a.displayOrder !== undefined && a.displayOrder !== null ? a.displayOrder : Infinity;
            const orderB = b.displayOrder !== undefined && b.displayOrder !== null ? b.displayOrder : Infinity;
            if (orderA !== orderB) return orderA - orderB;
            return (b.uploadedAt?.getTime() || 0) - (a.uploadedAt?.getTime() || 0);
          }).map((template) => (
            <div
              key={template.id}
              className={`border rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${selectedTemplate?.id === template.id
                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => handleTemplateSelect(template)}
            >
              {/* Preview Image */}
              {template.previewImage && template.previewImage !== '/api/placeholder/300/200' ? (
                <div className="mb-3 rounded-lg overflow-hidden bg-gray-100 h-24">
                  <img
                    src={template.previewImage}
                    alt={template.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="mb-3 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 h-24 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              )}

              {/* Template Info */}
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium text-gray-900 text-sm line-clamp-1 flex-1">{template.name}</h3>
                {selectedTemplate?.id === template.id && (
                  <span className="ml-2 flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>

              <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                {template.description || 'No description'}
              </p>

              {/* Tags */}
              {template.tags && template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {template.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="px-2 py-0.5 text-xs text-gray-400">
                      +{template.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create New Template Button */}
      <div className="border-t pt-4">
        <button
          type="button"
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-colors"
          onClick={() => {
            // Navigate to template editor
            window.open('/templates/create', '_blank');
          }}
        >
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Template
          </div>
        </button>
      </div>
    </div>
  );
};

export default EmailTemplateSelector;
